/**
 * Settings Service (Frontend Web) - Read-only access to app settings
 */

const API_BASE = import.meta.env.VITE_API_URL || 'https://apicabaza.creago.be';

// In-memory cache for settings (avoids repeated API calls)
let settingsCache = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get a setting value by code for a specific language
 * @param {string} code - Setting code (e.g. 'SLOGAN')
 * @param {string} lang - Language code (e.g. 'fr')
 * @returns {Promise<string|null>} Setting value or null
 */
export const getSettingValue = async (code, lang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/settings/${code}`);
        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        if (success) {
            const data = result.data ?? result.DATA;
            const key = `value_${lang}`;
            return data[key] || data[`VALUE_${lang.toUpperCase()}`] || data.value_fr || data.VALUE_FR || null;
        }
        return null;
    } catch (error) {
        console.error('Error fetching setting:', error);
        return null;
    }
};

/**
 * Lit la configuration du mode Bêta depuis les settings.
 * @param {string} lang - Code langue
 * @returns {Promise<{enabled: boolean, endDate: Date|null, message: string, graceDays: number}>}
 */
export const getBetaConfig = async (lang = 'fr') => {
    const [enabled, endDate, message, graceDays] = await Promise.all([
        getSettingValue('BETA_ENABLED', lang),
        getSettingValue('BETA_END_DATE', lang),
        getSettingValue('BETA_MESSAGE', lang),
        getSettingValue('BETA_GRACE_DAYS', lang)
    ]);

    let parsedDate = null;
    if (endDate) {
        const d = new Date(endDate);
        if (!isNaN(d.getTime())) {
            parsedDate = d;
        }
    }

    let parsedGrace = 0;
    if (graceDays !== null && graceDays !== undefined && !isNaN(parseInt(graceDays, 10))) {
        parsedGrace = parseInt(graceDays, 10);
    }

    // L'API renvoie la valeur typee par SQL Server : BETA_ENABLED arrive en
    // nombre (1) et non en chaine ('1'), donc une comparaison stricte echouerait.
    const enabledFlag = String(enabled === null || enabled === undefined ? '' : enabled)
        .trim()
        .toLowerCase();

    return {
        enabled: enabledFlag === '1' || enabledFlag === 'true',
        endDate: parsedDate,
        message: message || '',
        graceDays: parsedGrace
    };
};
